import React, { useState, useEffect, memo, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import { ToastProvider } from './context/ToastContext';
import { ModeProvider } from './context/ModeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';
import './App.css';

// Lazy-load components not needed on initial render
const GuidedTour = lazy(() => import('./components/GuidedTour'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const FoundingRegister = lazy(() => import('./pages/FoundingRegister'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const SubscriptionSuccess = lazy(() => import('./pages/SubscriptionSuccess'));
const SubscriptionCancelled = lazy(() => import('./pages/SubscriptionCancelled'));
const ActivateAccount = lazy(() => import('./pages/ActivateAccount'));
const Contact = lazy(() => import('./pages/Contact'));
const Help = lazy(() => import('./pages/Help'));

// Code-split large pages for better performance
const Jobs = lazy(() => import('./pages/Jobs'));
const NewJob = lazy(() => import('./pages/NewJob'));
const EditJob = lazy(() => import('./pages/EditJob'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const MyBids = lazy(() => import('./pages/MyBids'));
const MyTickets = lazy(() => import('./pages/MyTickets'));
const Messages = lazy(() => import('./pages/Messages'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ContractorReviews = lazy(() => import('./pages/ContractorReviews'));
const BrowseContractors = lazy(() => import('./pages/BrowseContractors'));
const Subscription = lazy(() => import('./pages/Subscription'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ContractorPortfolio = lazy(() => import('./pages/ContractorPortfolio'));
const ContractorProfile = lazy(() => import('./pages/ContractorProfile'));
const HomeownerProfile = lazy(() => import('./pages/HomeownerProfile'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Invoices = lazy(() => import('./pages/Invoices'));
const SimpleInvoice = lazy(() => import('./pages/SimpleInvoice'));
const InvoiceView = lazy(() => import('./pages/InvoiceView'));
const Agreements = lazy(() => import('./pages/Agreements'));
const Tools = lazy(() => import('./pages/Tools'));
const DIY = lazy(() => import('./pages/DIY'));
const DIYTools = lazy(() => import('./pages/DIYTools'));
const JobAlerts = lazy(() => import('./pages/JobAlerts'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Drawing = lazy(() => import('./pages/Drawing'));
const GiveawayRules = lazy(() => import('./pages/GiveawayRules'));
const GiveawayInfo = lazy(() => import('./pages/GiveawayInfo'));
const AdminGiveaway = lazy(() => import('./pages/AdminGiveaway'));
const AdminNewsletter = lazy(() => import('./pages/AdminNewsletter'));
const BlogAdmin = lazy(() => import('./pages/BlogAdmin'));
const BlogEditor = lazy(() => import('./pages/BlogEditor'));
const PartnerPromos = lazy(() => import('./pages/PartnerPromos'));
const HOACommunity = lazy(() => import('./pages/HOACommunity'));
const QuickPost = lazy(() => import('./pages/QuickPost'));
const JobManage = lazy(() => import('./pages/JobManage'));
const AdminPostJob = lazy(() => import('./pages/AdminPostJob'));
const AdminContractorLeads = lazy(() => import('./pages/AdminContractorLeads'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const InstantBookings = lazy(() => import('./pages/InstantBookings'));

// Loading fallback component
const PageLoader = () => (
  <div className="loading-screen" style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh'
  }}>
    <div>Loading...</div>
  </div>
);

// Memoize Navbar and Footer to prevent re-renders when auth state changes
const MemoizedNavbar = memo(Navbar);
const MemoizedFooter = memo(Footer);

// Protected Route wrapper - uses overlay to prevent unmount/remount flickering
const ProtectedRoute = ({ children, allowPending = false }) => {
  const { isAuthenticated, loading, user } = useAuth();

  // If still loading, show overlay but keep structure
  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '50vh' }}>
        <div className="loading-screen" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Redirect pending contractors to activation page (unless they're already there)
  if (!allowPending && user?.user_type === 'contractor' && user?.subscription_status === 'pending') {
    return <Navigate to="/activate-account" replace />;
  }

  return children;
};

// Contractor-only Route wrapper
const ContractorRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user?.user_type !== 'contractor') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Public Route wrapper (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

// Token Handler - processes tokens from marketing site redirect
function TokenHandler({ children }) {
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userData = urlParams.get('user');

    if (token && userData) {
      // Store token and user in localStorage
      const decodedToken = decodeURIComponent(token);
      const decodedUser = decodeURIComponent(userData);

      localStorage.setItem('token', decodedToken);
      localStorage.setItem('user', decodedUser);

      // Remove token from URL for security and reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);

      // Reload to trigger AuthContext with new token
      window.location.reload();
    } else {
      setProcessing(false);
    }
  }, []);

  if (processing) {
    return <div className="loading-screen">Loading...</div>;
  }

  return children;
}

// External Redirect - for redirecting to marketing site (kept for future use)
// eslint-disable-next-line no-unused-vars
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);

  return <div className="loading-screen">Redirecting...</div>;
}

function AppContent() {
  const { user, isAuthenticated, loading } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);

  // Show welcome modal for first-time users
  useEffect(() => {
    if (isAuthenticated && user && !localStorage.getItem('hasSeenGuidedTour')) {
      // Small delay to let the dashboard load first
      const timer = setTimeout(() => {
        setShowWelcome(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user]);

  // Memoize the onShowHelp callback to prevent Navbar re-renders
  const handleShowHelp = useCallback(() => setShowWelcome(true), []);

  return (
    <div className="App">
      {/* L14 fix: Skip navigation link for keyboard accessibility */}
      <a href="#main-content" className="skip-nav">
        Skip to main content
      </a>
      <MemoizedNavbar onShowHelp={handleShowHelp} />
      <main id="main-content" className="app-content">
      {/* M15 fix: Wrap lazy routes with ErrorBoundary for per-route error handling */}
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            loading ? (
              <div className="loading-screen">Loading...</div>
            ) : isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        {/* Redirect /signup to /register for consistency */}
        <Route path="/signup" element={<Navigate to="/register" replace />} />
        <Route
          path="/register/founding"
          element={
            <PublicRoute>
              <FoundingRegister />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/contact" element={<Contact />} />

        {/* Giveaway Public Routes */}
        <Route path="/giveaway" element={<GiveawayInfo />} />
        <Route path="/giveaway/leaderboard" element={<Leaderboard />} />
        <Route path="/giveaway/drawing" element={<Drawing />} />
        <Route path="/giveaway/rules" element={<GiveawayRules />} />
                {/* Quick Post Public Routes (Beta Mode) */}
        <Route path="/quick-post" element={<QuickPost />} />
        <Route path="/job/manage/:token" element={<JobManage />} />

        {/* Help Page */}
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <Help onShowTutorial={handleShowHelp} />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs"
          element={
            <ProtectedRoute>
              <Jobs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/new"
          element={
            <ProtectedRoute>
              <NewJob />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/:id/edit"
          element={
            <ProtectedRoute>
              <EditJob />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/:id"
          element={
            <ProtectedRoute>
              <JobDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hoa-community"
          element={
            <ProtectedRoute>
              <HOACommunity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-bids"
          element={
            <ProtectedRoute>
              <MyBids />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-tickets"
          element={
            <ProtectedRoute>
              <MyTickets />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools"
          element={
            <ContractorRoute>
              <Tools />
            </ContractorRoute>
          }
        />
        <Route
          path="/job-alerts"
          element={
            <ContractorRoute>
              <JobAlerts />
            </ContractorRoute>
          }
        />
        <Route
          path="/diy"
          element={
            <ProtectedRoute>
              <DIY />
            </ProtectedRoute>
          }
        />
        <Route
          path="/diy-tools"
          element={
            <ProtectedRoute>
              <DIYTools />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contractors"
          element={
            <ProtectedRoute>
              <BrowseContractors />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contractors/:contractorId/reviews"
          element={
            <ProtectedRoute>
              <ContractorReviews />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contractors/:contractorId"
          element={
            <ProtectedRoute>
              <ContractorProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/homeowners/:homeownerId"
          element={
            <ProtectedRoute>
              <HomeownerProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activate-account"
          element={
            <ProtectedRoute allowPending={true}>
              <ActivateAccount />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <Subscription />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription/success"
          element={
            <ProtectedRoute allowPending={true}>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription/cancelled"
          element={
            <ProtectedRoute allowPending={true}>
              <SubscriptionCancelled />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/giveaways"
          element={
            <ProtectedRoute>
              <AdminGiveaway />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/newsletter"
          element={
            <ProtectedRoute>
              <AdminNewsletter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog"
          element={
            <ProtectedRoute>
              <BlogAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog/new"
          element={
            <ProtectedRoute>
              <BlogEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog/edit/:id"
          element={
            <ProtectedRoute>
              <BlogEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/partner-promos"
          element={
            <ProtectedRoute>
              <PartnerPromos />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/post-job"
          element={
            <ProtectedRoute>
              <AdminPostJob />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/contractor-leads"
          element={
            <ProtectedRoute>
              <AdminContractorLeads />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio"
          element={
            <ProtectedRoute>
              <ContractorPortfolio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <Invoices />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices/new"
          element={
            <ContractorRoute>
              <SimpleInvoice />
            </ContractorRoute>
          }
        />
        <Route
          path="/invoices/edit/:id"
          element={
            <ContractorRoute>
              <SimpleInvoice />
            </ContractorRoute>
          }
        />
        <Route
          path="/invoices/:id"
          element={
            <ProtectedRoute>
              <InvoiceView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agreements"
          element={
            <ProtectedRoute>
              <Agreements />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance"
          element={
            <ProtectedRoute>
              <Maintenance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/instant-bookings"
          element={
            <ProtectedRoute>
              <InstantBookings />
            </ProtectedRoute>
          }
        />

        {/* 404 Not Found - Catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      </main>
      <MemoizedFooter />
      <Suspense fallback={null}>
        <CookieConsent />
        {showWelcome && (
          <GuidedTour
            user={user}
            isOpen={showWelcome}
            onClose={() => setShowWelcome(false)}
          />
        )}
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <Router>
      <TokenHandler>
        <AuthProvider>
          <ModeProvider>
            <WebSocketProvider>
              <ToastProvider>
                <ErrorBoundary>
                  <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="light"
                  />
                  <AppContent />
                </ErrorBoundary>
              </ToastProvider>
            </WebSocketProvider>
          </ModeProvider>
        </AuthProvider>
      </TokenHandler>
    </Router>
  );
}

export default App;
